/**
 * @file promotion-ui.js
 * @description UI rendering components for the promotion system
 * @module GniibPromotions.UI
 *
 * Contains ProductRenderer, SummaryRenderer, MobileSummary, and UsageDisplay.
 */
(function(global) {
  'use strict';

  var GP = global.GniibPromotions = global.GniibPromotions || {};
  var Utils = GP.Utils;
  var CartManager = GP.CartManager;

  /**
   * UI namespace
   * @namespace GniibPromotions.UI
   */
  GP.UI = {};

  // ============================================
  // Product Renderer
  // ============================================
  GP.UI.ProductRenderer = {

    /**
     * Render all products to the grid
     * @param {Object} ctx - Promotion context
     */
    render: function(ctx) {
      var productsGridEl = ctx.elements.PRODUCTS_GRID;
      if (!productsGridEl) return;

      productsGridEl.innerHTML = '';

      for (var i = 0; i < ctx.cart.productsData.length; i++) {
        var product = ctx.cart.productsData[i];
        var productCard = this.createProductCard(ctx, product);
        productsGridEl.appendChild(productCard);
      }
    },

    /**
     * Create a product card element
     * @param {Object} ctx - Promotion context
     * @param {Object} product - Product data
     * @returns {HTMLElement} Product card element
     */
    createProductCard: function(ctx, product) {
      var card = document.createElement('div');
      card.className = 'product-card';
      card.dataset.productId = product.id;

      var defaultVariant = product.variants[0];
      var priceHtml = this.getPriceHtml(ctx, product, defaultVariant);
      var variantSelector = this.createVariantSelector(product);

      var productImage = product.featured_image || '/assets/placeholder.svg';

      card.innerHTML = '<div class="product-image">' +
        '<img src="' + productImage + '" alt="' + product.title + '" loading="lazy">' +
        '</div>' +
        '<div class="product-info">' +
        '<h3 class="product-title">' + product.title + '</h3>' +
        '<div class="product-price">' + priceHtml + '</div>' +
        variantSelector +
        '<div class="product-actions">' +
        '<div class="quantity-selector">' +
        '<button class="quantity-btn" data-action="decrease">-</button>' +
        '<input type="number" class="quantity-input" value="1" min="1" max="10">' +
        '<button class="quantity-btn" data-action="increase">+</button>' +
        '</div>' +
        '<button class="add-to-cart-btn" data-product-id="' + product.id + '" data-variant-id="' + defaultVariant.id + '">' +
        '加入' +
        '</button>' +
        '</div>' +
        '</div>';

      this.bindProductCardEvents(ctx, card);
      return card;
    },

    /**
     * Get price HTML using handler
     * @param {Object} ctx - Promotion context
     * @param {Object} product - Product data
     * @param {Object} variant - Variant data
     * @returns {string} Price HTML
     */
    getPriceHtml: function(ctx, product, variant) {
      if (ctx.handler.renderPriceHtml) {
        return ctx.handler.renderPriceHtml(ctx, product, variant);
      }
      // Default price display
      return '<span class="product-price-full">$' + Utils.formatPrice(variant.price) + '</span>';
    },

    /**
     * Create variant selector HTML
     * @param {Object} product - Product data
     * @returns {string} Variant selector HTML
     */
    createVariantSelector: function(product) {
      if (product.variants.length <= 1) return '';

      var html = '<select class="variant-selector">';
      for (var i = 0; i < product.variants.length; i++) {
        var variant = product.variants[i];
        html += '<option value="' + variant.id + '">' + variant.title + '</option>';
      }
      html += '</select>';
      return html;
    },

    /**
     * Bind events to product card
     * @param {Object} ctx - Promotion context
     * @param {HTMLElement} card - Product card element
     */
    bindProductCardEvents: function(ctx, card) {
      var self = this;
      var quantityInput = card.querySelector('.quantity-input');
      var quantityBtns = card.querySelectorAll('.quantity-btn');
      var addToCartBtn = card.querySelector('.add-to-cart-btn');
      var variantSelector = card.querySelector('.variant-selector');

      // Variant change
      if (variantSelector) {
        variantSelector.addEventListener('change', function() {
          addToCartBtn.dataset.variantId = this.value;
          var productId = addToCartBtn.dataset.productId;
          var product = CartManager.findProduct(ctx.cart, productId);
          var variant = CartManager.findVariant(product, this.value);

          if (product && variant) {
            var priceContainer = card.querySelector('.product-price');
            priceContainer.innerHTML = self.getPriceHtml(ctx, product, variant);
          }
        });
      }

      // Quantity buttons
      quantityBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var action = this.dataset.action;
          var currentValue = parseInt(quantityInput.value) || 1;

          if (action === 'increase' && currentValue < 10) {
            quantityInput.value = currentValue + 1;
          } else if (action === 'decrease' && currentValue > 1) {
            quantityInput.value = currentValue - 1;
          }
        });
      });

      // Add to cart
      addToCartBtn.addEventListener('click', function() {
        var productId = this.dataset.productId;
        var variantId = this.dataset.variantId;
        var quantity = parseInt(quantityInput.value) || 1;

        ctx.addToSelection(productId, variantId, quantity);
        quantityInput.value = 1;
      });
    },

    /**
     * Update product availability display
     * @param {Object} ctx - Promotion context
     */
    updateAvailability: function(ctx) {
      var cards = document.querySelectorAll('.product-card');
      var self = this;

      cards.forEach(function(card) {
        var productId = card.dataset.productId;
        var product = CartManager.findProduct(ctx.cart, productId);
        if (!product) return;

        var variant = product.variants[0];
        var priceContainer = card.querySelector('.product-price');
        if (priceContainer) {
          priceContainer.innerHTML = self.getPriceHtml(ctx, product, variant);
        }
      });
    }
  };

  // ============================================
  // Summary Renderer
  // ============================================
  GP.UI.SummaryRenderer = {

    /**
     * Update selected items display
     * @param {Object} ctx - Promotion context
     */
    updateSelectedItems: function(ctx) {
      var selectedItemsEl = ctx.elements.SELECTED_ITEMS;
      if (!selectedItemsEl) return;

      if (!CartManager.hasItems(ctx.cart)) {
        selectedItemsEl.innerHTML = '<p class="empty-selection">尚未選擇產品</p>';
        return;
      }

      var html = '';
      var self = this;

      CartManager.forEach(ctx.cart, function(item, key) {
        html += self.renderSelectedItem(ctx, item, key);
      });

      // Add gift items for eligible promotions
      html += this.renderGiftItems(ctx);

      selectedItemsEl.innerHTML = html;
      this.bindRemoveButtons(ctx);
    },

    /**
     * Render a single selected item
     * @param {Object} ctx - Promotion context
     * @param {Object} item - Cart item
     * @param {string} key - Item key
     * @returns {string} Item HTML
     */
    renderSelectedItem: function(ctx, item, key) {
      // Check for handler-specific rendering
      if (ctx.handler.renderSelectedItem) {
        return ctx.handler.renderSelectedItem(ctx, item, key);
      }

      var productImage = item.product.featured_image || '/assets/placeholder.svg';
      var discountName = ctx.getDiscountNameForItem(item);
      var priceDisplay = this.getItemPriceDisplay(ctx, item);

      // Check for mixed pricing (FUBON, CITIBANK, IBD_GROUP)
      var hasMixedPricing = item.quantityWithDiscount > 0 && item.quantityFullPrice > 0;

      if (hasMixedPricing) {
        var discountLabel = this.getDiscountLabel(item);
        return '<div class="selected-item-group">' +
          '<div class="item-row-thumbnail">' +
          '<img class="item-thumbnail" src="' + productImage + '" alt="' + item.product.title + '">' +
          '<button class="remove-item" data-key="' + key + '" title="移除">×</button>' +
          '</div>' +
          '<div class="item-row-details">' +
          '<div class="item-info-group">' +
          '<div class="discount-name">' + discountName + '</div>' +
          '<div class="item-name">' + item.product.title + '</div>' +
          '<div class="item-details">' + item.variant.title + '</div>' +
          '<div class="item-line">× ' + item.quantityWithDiscount + ' (' + discountLabel + ') - 數量: ' + item.quantityWithDiscount + '</div>' +
          '<div class="item-line">× ' + item.quantityFullPrice + ' (原價) - 數量: ' + item.quantityFullPrice + '</div>' +
          '</div>' +
          '<div class="item-quantity">× ' + item.quantity + '</div>' +
          '<div class="item-price">' + priceDisplay + '</div>' +
          '</div>' +
          '</div>';
      }

      // Check for single-tier pricing with discount info (FUBON, CITIBANK, LIUYANG)
      var showDiscountLabel = (item.promotionType === 'fubon' || item.promotionType === 'citibank' || item.promotionType === 'liuyang');
      var detailsSuffix = '';
      if (showDiscountLabel) {
        if (item.quantityWithDiscount > 0) {
          detailsSuffix = ' (' + this.getDiscountLabel(item) + ')';
        } else if (item.quantityFullPrice > 0) {
          detailsSuffix = ' (原價)';
        } else if (item.hasDiscount) {
          detailsSuffix = ' (8折)';
        }
      }

      // CUMC rule label
      if (item.promotionType === 'cumc' && item.ruleApplied) {
        detailsSuffix = ' (' + (item.ruleApplied === 1 ? '規則一' : '規則二') + ')';
      }

      return '<div class="selected-item">' +
        '<div class="item-row-thumbnail">' +
        '<img class="item-thumbnail" src="' + productImage + '" alt="' + item.product.title + '">' +
        '<button class="remove-item" data-key="' + key + '" title="移除">×</button>' +
        '</div>' +
        '<div class="item-row-details">' +
        '<div class="item-info">' +
        '<div class="discount-name">' + discountName + '</div>' +
        '<div class="item-name">' + item.product.title + '</div>' +
        '<div class="item-details">' + item.variant.title + detailsSuffix + ' - 數量: ' + item.quantity + '</div>' +
        '</div>' +
        '<div class="item-quantity">× ' + item.quantity + '</div>' +
        '<div class="item-price">' + priceDisplay + '</div>' +
        '</div>' +
        '</div>';
    },

    /**
     * Get discount label for item
     * @param {Object} item - Cart item
     * @returns {string} Discount label
     */
    getDiscountLabel: function(item) {
      if (item.promotionType === 'fubon' || item.promotionType === 'citibank') {
        return item.discountPercentage === 30 ? '7折' : '8折';
      }
      if (item.promotionType === 'ibd_group') {
        return '75折';
      }
      if (item.promotionType === 'liuyang') {
        return '8折';
      }
      return '折扣';
    },

    /**
     * Get price display HTML for selected item
     * @param {Object} ctx - Promotion context
     * @param {Object} item - Cart item
     * @returns {string} Price display HTML
     */
    getItemPriceDisplay: function(ctx, item) {
      var itemTotal = ctx.calculateItemTotal(item);
      var originalSubtotal = item.basePrice * item.quantity;

      // Check if there's a discount
      if (itemTotal < originalSubtotal) {
        return '<span class="price-original">$' + Utils.formatPrice(originalSubtotal) + '</span>' +
               '<span class="price-discounted">$' + Utils.formatPrice(itemTotal) + '</span>';
      }

      return '<span class="item-price">$' + Utils.formatPrice(itemTotal) + '</span>';
    },

    /**
     * Render gift items for eligible promotions
     * @param {Object} ctx - Promotion context
     * @returns {string} Gift items HTML
     */
    renderGiftItems: function(ctx) {
      var html = '';
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);

      // SKHWC gift: M3XTRA 護腸配方（7天裝）× 2 when 8+ items and gift campaign available
      if (ctx.promotionType === 'skhwc_staff' && totalQuantity >= 8) {
        if (ctx.handler.isGiftCampaignAvailable && ctx.handler.isGiftCampaignAvailable(ctx)) {
          html += '<div class="selected-item gift-item">' +
            '<div class="item-info">' +
            '<div class="item-name">🎁 M3XTRA 護腸配方（7天裝）</div>' +
            '<div class="item-details">× 2 (免費贈品)</div>' +
            '</div>' +
            '<div class="item-price gift-price">免費</div>' +
            '</div>';
        }
      }

      // Liuyang gift: 纖肝代謝配方（7天裝）based on quantity (every 8 items = 4 gifts)
      if (ctx.promotionType === 'liuyang') {
        var gifts = ctx.handler.calculateGifts ? ctx.handler.calculateGifts(totalQuantity) : 0;
        if (gifts > 0) {
          var giftTitle = ctx.cart.promotionData?.promotion?.rule2?.giftVariantTitle || '纖肝代謝配方（7天裝）';
          html += '<div class="selected-item gift-item">' +
            '<div class="item-info">' +
            '<div class="item-name">🎁 ' + giftTitle + '</div>' +
            '<div class="item-details">× ' + gifts + ' (免費贈品)</div>' +
            '</div>' +
            '<div class="item-price gift-price">免費</div>' +
            '</div>';
        }
      }

      return html;
    },

    /**
     * Bind remove button events
     * @param {Object} ctx - Promotion context
     */
    bindRemoveButtons: function(ctx) {
      var removeButtons = document.querySelectorAll('.remove-item');
      removeButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var key = this.dataset.key;
          ctx.removeFromSelection(key);
        });
      });
    },

    /**
     * Update subtotal display
     * @param {Object} ctx - Promotion context
     */
    updateSubtotal: function(ctx) {
      var subtotal = CartManager.calculateSubtotal(ctx.cart, function(item) {
        return ctx.calculateItemTotal(item);
      });

      if (ctx.elements.SUBTOTAL) {
        ctx.elements.SUBTOTAL.textContent = '$' + Utils.formatPrice(subtotal);
      }
    },

    /**
     * Update checkout button state
     * @param {Object} ctx - Promotion context
     */
    updateCheckoutButton: function(ctx) {
      var hasItems = CartManager.hasItems(ctx.cart);

      if (ctx.elements.CHECKOUT_BTN) {
        ctx.elements.CHECKOUT_BTN.disabled = !hasItems;
      }

      if (ctx.elements.MOBILE_CHECKOUT_BTN) {
        ctx.elements.MOBILE_CHECKOUT_BTN.disabled = !hasItems;
      }
    }
  };

  // ============================================
  // Mobile Summary
  // ============================================
  GP.UI.MobileSummary = {

    /**
     * Toggle mobile summary
     * @param {Object} ctx - Promotion context
     */
    toggle: function(ctx) {
      var mobileSummaryEl = ctx.elements.MOBILE_SUMMARY;
      if (!mobileSummaryEl) return;

      if (mobileSummaryEl.classList.contains('expanded')) {
        this.close(ctx);
      } else {
        this.open(ctx);
      }
    },

    /**
     * Open mobile summary
     * @param {Object} ctx - Promotion context
     */
    open: function(ctx) {
      var mobileSummaryEl = ctx.elements.MOBILE_SUMMARY;
      var backdropEl = ctx.elements.SUMMARY_BACKDROP;

      if (mobileSummaryEl) {
        mobileSummaryEl.classList.add('expanded');
      }
      if (backdropEl) {
        backdropEl.classList.add('visible');
      }
      document.body.style.overflow = 'hidden';
    },

    /**
     * Close mobile summary
     * @param {Object} ctx - Promotion context
     */
    close: function(ctx) {
      var mobileSummaryEl = ctx.elements.MOBILE_SUMMARY;
      var backdropEl = ctx.elements.SUMMARY_BACKDROP;

      if (mobileSummaryEl) {
        mobileSummaryEl.classList.remove('expanded');
      }
      if (backdropEl) {
        backdropEl.classList.remove('visible');
      }
      document.body.style.overflow = '';
    },

    /**
     * Update mobile summary content
     * @param {Object} ctx - Promotion context
     */
    update: function(ctx) {
      var totalQuantity = CartManager.getTotalQuantity(ctx.cart);
      var subtotal = CartManager.calculateSubtotal(ctx.cart, function(item) {
        return ctx.calculateItemTotal(item);
      });

      if (ctx.elements.MOBILE_ITEM_COUNT) {
        ctx.elements.MOBILE_ITEM_COUNT.textContent = totalQuantity;
      }

      if (ctx.elements.MOBILE_SUBTOTAL) {
        ctx.elements.MOBILE_SUBTOTAL.textContent = '$' + Utils.formatPrice(subtotal);
      }

      if (ctx.elements.MOBILE_SUBTOTAL_AMOUNT) {
        ctx.elements.MOBILE_SUBTOTAL_AMOUNT.textContent = '$' + Utils.formatPrice(subtotal);
      }

      // Update selected items in mobile view
      this.updateSelectedItems(ctx);
    },

    /**
     * Update selected items in mobile summary
     * @param {Object} ctx - Promotion context
     */
    updateSelectedItems: function(ctx) {
      var mobileSelectedItemsEl = ctx.elements.MOBILE_SELECTED_ITEMS;
      if (!mobileSelectedItemsEl) return;

      if (!CartManager.hasItems(ctx.cart)) {
        mobileSelectedItemsEl.innerHTML = '<p class="empty-selection">尚未選擇產品</p>';
        return;
      }

      var html = '';
      CartManager.forEach(ctx.cart, function(item, key) {
        html += GP.UI.SummaryRenderer.renderSelectedItem(ctx, item, key);
      });

      // Add gift items for eligible promotions
      html += GP.UI.SummaryRenderer.renderGiftItems(ctx);

      mobileSelectedItemsEl.innerHTML = html;

      // Bind remove buttons for mobile
      var removeButtons = mobileSelectedItemsEl.querySelectorAll('.remove-item');
      removeButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var key = this.dataset.key;
          ctx.removeFromSelection(key);
        });
      });
    }
  };

  // ============================================
  // Usage Display
  // ============================================
  GP.UI.UsageDisplay = {

    /**
     * Update usage display
     * @param {Object} ctx - Promotion context
     */
    update: function(ctx) {
      var usageInfoEl = document.querySelector('.usage-info');
      if (!usageInfoEl) return;

      // Use handler-specific usage display
      if (ctx.handler.renderUsageDisplay) {
        ctx.handler.renderUsageDisplay(ctx, usageInfoEl);
        return;
      }

      // Default: hide usage display
      usageInfoEl.style.display = 'none';
    }
  };

})(window);
